import type {Constructor, Fn, Fnify, Nullable, Nullish, Rec} from "./type.mjs";

export type TypeCheckcList = "function" | "string" | "boolean" | "number" | "bigint" | "object" | "symbol";

export function is(value: unknown, type: "symbol"): value is symbol;
export function is(value: unknown, type: "undefined"): value is undefined;
export function is<T extends Rec>(value: unknown, type: "object"): value is T | null;
export function is(value: unknown, type: "bigint"): value is bigint;
export function is(value: unknown, type: "number"): value is number;
export function is(value: unknown, type: "boolean"): value is boolean;
export function is(value: unknown, type: "string"): value is string;
export function is<T extends Fn<any[], any>>(value: unknown, type: "function"): value is T;
export function is<T extends TypeCheckcList>(value: unknown, type: T): boolean {
    return typeof value === type;
}

export function truify<T>(value: T): value is NonNullable<T> {
    return Boolean(value);
}

export function isInstance<T extends Rec>(value: unknown): value is T {
    return typeof value === "object" && value !== null;
}

export function when<T, R1>(value: T, then: Fn<[NonNullable<T>], R1> | R1): R1 | undefined;
export function when<T, R1, R2>(value: T, then: Fn<[NonNullable<T>], R1> | R1, fallback: Fnify<R2>): R1 | R2;
export function when<T, R1, R2>(
    value: T,
    then: Fn<[NonNullable<T>], R1> | R1,
    fallback?: Fnify<R2>,
): R1 | R2 | undefined {
    return value ? (is(then, "function") ? then(value) : then) : is(fallback, "function") ? fallback() : fallback;
}

export function fnify<T>(value: Fnify<T>): Fn<[], T> {
    return is(value, "function") ? value : () => value;
}

export function defnify<R>(value: Fnify<R>): R {
    return is(value, "function") ? value() : value;
}

export function isNullable<T>(value: Nullable<T>): value is null {
    return value === null;
}

export function isNullish<T>(value: Nullish<T>): value is null | undefined {
    return value === null || value === undefined;
}

export function invoke<T, A extends any[]>(target: Fn<A, T>, ...args: A): T;
export function invoke<T>(target: T, ...args: any[]): T;
export function invoke(target: any, ...args: any[]): any {
    return is(target, "function") ? target(...args) : target;
}

const onceRef = new WeakMap<Fn<[], any>, any>();

function once<R>(fn: Fn<[], R>): Fn<[], R> {
    return (): R => {
        const res = onceRef.get(fn) ?? fn();
        if (!onceRef.has(fn)) {
            onceRef.set(fn, res);
        }

        return res;
    };
}

function toStringValue(value: unknown): string {
    if (value === null) return "null";
    if (value === undefined) return "undefined";

    if (typeof value === "bigint") {
        value = String(value).concat("n");
    }

    if (typeof value === "symbol") {
        value = value.toString();
    }

    if (value instanceof Date) {
        try {
            return value.toISOString();
        } catch {
            return value.toString();
        }
    }

    try {
        return JSON.stringify(value);
    } catch {
        return Object.prototype.toString.call(value);
    }
}

function arrow<F extends Fn<any[], any>>(name: string, fn: F): F {
    return {[name]: (...args: any[]) => fn(...args)}[name] as F;
}

export type NamedObject = Fn<any[], any> | {new (): unknown};

function named<T extends NamedObject>(name: string, target: T): T {
    const isClass = is(target, "function") && Function.prototype.toString.call(target).startsWith("class ");

    return is(target, "function") && !isClass
        ? arrow(name, target)
        : ({[name]: class extends (target as any) {}}[name] as T);
}

function construct<T extends {constructor: Function} | Function, A extends any[]>(target: T, ...args: A): T {
    const ctor = (is(target, "function") ? target : target.constructor) as Constructor<T>;

    return new ctor(...args);
}

function combine<V1, V2, R>(fn1: Fn<[V1], V2>, fn2: Fn<[V2], R>): Fn<[V1], R> {
    return (v1: V1) => fn2(fn1(v1));
}

export const fn = {
    is,
    once,
    when,
    fnify,
    arrow,
    named,
    defnify,
    invoke,
    isInstance,
    isNullable,
    isNullish,
    toStringValue,
    construct,
    truify,
    combine,
};
